import { assetUrl } from '@/utils/asset-url';
import { ReactComponent as IconDataRequests } from "@/assets/svgs/icon-data-requests.svg";
import { ReactComponent as IconData } from "@/assets/svgs/icon-data.svg";
import { ReactComponent as IconGear } from "@/assets/svgs/icon-gear.svg";
import { ReactComponent as IconUseCases } from "@/assets/svgs/icon-use-cases.svg";
import Button from "@/components/Button";
import Toast from "@/components/Toast";
import useModal from "@/hooks/use-modal";
import usePage from "@/hooks/use-page";
import useRequests from "@/hooks/use-requests";
import useStatus from "@/hooks/use-status";
import useToast from "@/hooks/use-toast";
import { useTradex } from "@/hooks/use-tradex/index";
import useUseCases from "@/hooks/use-use-cases";
import { NavLink, useNavigate } from "@/lib/router-dom";
import API_DATA_ELEMENTS_REQUEST from "@/services/api/dataElementsEnrolment";
import { feature, features as featureConstant } from "@/utils/feature-verifier";
import handleSubPrincipals from "@/utils/handle-subscriptionPrincipal";
import rem from "@/utils/rem";
import titleCase from "@/utils/title-case";
import { Box, Grid } from "@dextech-ai/dex-design-system";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import RequestConfirmationModal from '@/dsComponents/RequestConfirmationModal';


const Page = () => {
	/*=================================================
		General
	/================================================*/

	const user = useSelector((state) => state.auth.user);
	const isAgentPrincipalPhase2Enabled = feature(featureConstant.AGENT_PRINCIPAL_PHASE_2)

	const statuses = {
		approved: "APPROVED",
		rejected: "REJECTED",
		revoked: "REVOKED",
		pending: "PENDING",
	};

	const userRole = {
		SUPER_ADMIN: "Super Admin",
		ADMIN: "Admin User",
		TECH_USER: "Tech User",
		OPS_USER: "Operation User",
	};

	const toastType = {
		success: "success",
		error: "error",
	};

	const { breadcrumbsItems } = usePage({
		title: "Dashboard",
		breadcrumbs: ["Dashboard"],
	});
	const { goTo } = useNavigate();

	const [requestType, setRequestType] = useState();

	const itemTypeNamingWrapper = (name) => {
		switch (name) {
			case "Provide_on_behalf":
				return "Provide on behalf";
			case "Consume_on_behalf":
				return "Consume on behalf";
			case "Consume_on_behalf_of_principal":
				return isAgentPrincipalPhase2Enabled ? "Request to consume on behalf (Principal)" : "Consume as agent";
			case "Provide_on_behalf_of_principal":
				return isAgentPrincipalPhase2Enabled ? "Request to provide on behalf (Principal)" : "Provide as agent";
			default:
				return name;
		}
	};

	/*=================================================
		API
	/================================================*/

	const { toastList, addToastMessage } = useToast();

	const {
		items: statusItems,
		fetch: fetchStatus,
		myPitstops,
		fetchMyPitstops,
		error: errorStatus,
	} = useStatus();
	const {
		items: useCaseList,
		fetch: fetchUseCases,
		error: errorUseCase,
	} = useUseCases();
	const {
		items: reviewItems,
		setItems: setReviewItems,
		fetch: fetchReview,
		meta: reviewMeta,
		error: errorReview,
		currentItem,
		view,
		error: errorRequest,
	} = useRequests([]);

	const getAllReview = {
		status: statuses.pending,
		isRequester: false,
	};

	useEffect(() => {
		async function getData() {
			fetchMyPitstops();
			fetchUseCases();
			fetchReview(getAllReview);
		}
		getData();
	}, []);

	useEffect(() => {
		window.dispatchEvent(new Event("resize"));
	}, [fetchMyPitstops, fetchUseCases]);

	useEffect(() => {
		addToastMessage(errorStatus, toastType.error);
	}, [errorStatus]);

	useEffect(() => {
		addToastMessage(errorUseCase, toastType.error);
	}, [errorUseCase]);

	useEffect(() => {
		addToastMessage(errorRequest, toastType.error);
	}, [errorRequest]);

	/*=================================================
		Pitstops
	/================================================*/

	const PitstopList = ({ data }) => {
		return (
			<div className="layout-dashboard__rows">
				{data.map((pitstop, index) => {
					return (
						<div className="layout-dashboard__row" key={index}>
							<Box display="flex" align="center">
								<div className="layout-dashboard__col">
									<Box display="flex" align="center">
										<div
											className="layout-dashboard__content__avatar"
											style={{
												backgroundImage: `url(${pitstop.image})`,
											}}
										></div>
										<div className="layout-dashboard__content__text">
											<div className="layout-dashboard__content__inline">
												<strong>{pitstop.name}</strong>
											</div>
										</div>
									</Box>
								</div>
							</Box>
						</div>
					);
				})}
				{data.length === 0 && (
					<>
						<div className="layout-dashboard__row">
							<div className="layout-dashboard__cols">
								<div className="layout-dashboard__col">
									<div className="layout-dashboard__col__content">
										<div className="layout-dashboard__content__text">
											There's no pitstop found
										</div>
									</div>
								</div>
							</div>
						</div>
					</>
				)}
			</div>
		);
	};

	/*=================================================
		Data Requests
	/================================================*/

	const DataRequestList = ({ data }) => {
		const list = data.map((item, index) => {
			return (
				index < 5 && (
					<div className="layout-dashboard__row" key={index}>
						<div className="layout-dashboard__cols">
							<div className="layout-dashboard__col">
								<div className="layout-dashboard__col__content">
									<div
										className="layout-dashboard__content__avatar"
										style={{
											backgroundImage: `url(${item?.participant?.iconUrl})`,
										}}
									></div>
									<div className="layout-dashboard__content__text">
										<div className="layout-dashboard__content__top">
											{item?.dataElement?.elementName}
										</div>
										<div className="layout-dashboard__content__bottom">
											<div className="layout-dashboard__content__bottom__cols">
												<div className="layout-dashboard__content__bottom__col layout-dashboard__content__bottom__col--fixed-width">
													{itemTypeNamingWrapper(
														titleCase(item?.type)
													)}
												</div>
												<div className="layout-dashboard__content__bottom__col">
													{moment(
														item?.created
													).format("DD MMM YYYY")}
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div className="layout-dashboard__col">
								{(item.participant?.subscriptionPrincipals?.some((p) => {
									return p?.status === "PENDING"
								}) || (item?.status === statuses.pending)) && (
										<>
											<div className="layout-dashboard__col__buttons">
												<Button
													text="Approve"
													maxWidth="120"
													sizing="mini"
													submit={() => {
														setRequestType(
															statuses.approved
														);
														showConfirmation();
														view(item.id);
													}}
												/>
												<Button
													text="Decline"
													ghost
													maxWidth="90"
													sizing="mini"
													submit={() => {
														setRequestType(
															statuses.rejected
														);
														showConfirmation();
														view(item.id);
													}}
												/>
											</div>
										</>
									)}
							</div>
						</div>
					</div>
				)
			);
		});
		return (
			<div className="layout-dashboard__rows">
				{list.length > 0 ? (
					list
				) : (
					<div className="layout-dashboard__row">
						<Box display="flex" align="center">
							<div className="layout-dashboard__col">
								<Box display="flex" align="center">
									<div className="layout-dashboard__content__text">
										<div className="layout-dashboard__content__top">
											There are no requests.
										</div>
									</div>
								</Box>
							</div>
						</Box>
					</div>
				)}
			</div>
		);
	};

	/*=================================================
		Use Cases
	/================================================*/

	const UseCaseList = ({ data }) => {
		const list = data
			.filter((useCase) => useCase.status === "enrolled")
			.map((useCase, index) => {
				return (
					index < 5 && (
						<div className="layout-dashboard__row" key={index}>
							<Box display="flex" align="center">
								<div className="layout-dashboard__col">
									<Box display="flex" align="center">
										<div className="layout-dashboard__content__text">
											<div className="layout-dashboard__content__top">
												{useCase.name}
											</div>

											{(useCase.provideOnBehalf > 0 ||
												useCase.contributes > 0 ||
												useCase.consumes > 0 ||
												useCase.consumeOnBehalf >
												0) && (
													<div
														className="layout-dashboard__content__bottom"
														style={{
															display: "flex",
															alignItems: "center",
														}}
													>
														<div
															className="layout-dashboard__content__icon"
															style={{
																marginRight:
																	rem(10),
															}}
														>
															<IconData />
														</div>
														<div className="layout-dashboard__content__caption">
															{useCase.provideOnBehalf >
																0 && (
																	<>
																		Providing (
																		{
																			useCase.provideOnBehalf
																		}
																		),
																	</>
																)}{" "}
															{useCase.contributes >
																0 && (
																	<>
																		Contributing (
																		{
																			useCase.contributes
																		}
																		),
																	</>
																)}{" "}
															{useCase.consumes >
																0 && (
																	<>
																		Consuming (
																		{
																			useCase.consumes
																		}
																		),
																	</>
																)}{" "}
															{useCase.consumeOnBehalf >
																0 && (
																	<>
																		Consumes On
																		Behalf (
																		{
																			useCase.consumeOnBehalf
																		}
																		)
																	</>
																)}
														</div>
													</div>
												)}
										</div>
										<div style={{ marginLeft: "auto" }}>
											<NavLink
												to={`/use-cases/${useCase.id}/dashboard`}
											>
												<Button
													text="Manage"
													id="dashboard-use-case-view-dashboard-button"
													ghost
												/>
											</NavLink>
										</div>
									</Box>
								</div>
							</Box>
						</div>
					)
				);
			});

		return (
			<div className="layout-dashboard__rows">
				{list.length > 0 ? (
					list
				) : (
					<div className="layout-dashboard__row">
						<div className="layout-dashboard__cols">
							<div className="layout-dashboard__col">
								<div className="layout-dashboard__col__content">
									<div className="layout-dashboard__content__text">
										<div className="layout-dashboard__content__top">
											You have not enroll to any use case
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	};

	/*=================================================
		Modal
	/================================================*/

	const {
		visible: visibleConfirmation,
		show: showConfirmation,
		hide: hideConfirmation,
	} = useModal();

	/*=================================================
		API Action Functions
	/================================================*/

	async function requestAction() {
		let approvedAllSubPrincipal = handleSubPrincipals(currentItem, statuses.approved)
		let rejectAllSubPrincipal = handleSubPrincipals(currentItem, statuses.rejected)

		const payload = {
			type: currentItem.type,
			organizationId: currentItem.organizationId,
			dataElementId: currentItem.dataElementId,
			useCaseId: currentItem.useCaseId,
		};

		if (requestType === statuses.approved) {
			payload.subscriptionPrincipals = approvedAllSubPrincipal;
		}

		if (requestType === statuses.rejected) {
			payload.subscriptionPrincipals = rejectAllSubPrincipal;
		}

		const [error, result] = await API_DATA_ELEMENTS_REQUEST.update(
			requestType,
			payload
		);

		if (error) {
			return addToastMessage(error?.message, toastType.error);
		}

		const request =
			requestType === statuses.approved ? "approved" : "rejected";
		addToastMessage(
			`You have successfully ${request} the data request.`,
			toastType.success
		);

		fetchReview(getAllReview);
	}

	const isTradex = useTradex();

	return (
		<>
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">Dashboard</div>
				</div>
			</div>

			<Grid
				className="layout-dashboard__container"
				columns={{ xs: '564px', sm: 'repeat(2, 564px)' }}
				columnGap={3}
				rowGap={3}
				alignItems="start"
			>
				{/* Left column: Pitstops */}
				<div className="layout-dashboard__block layout-dashboard__block--no-footer">
					<Box display="flex" wrap="wrap" align="center" pt={6} pb={4} px={4}>
						<div className="layout-dashboard__tile__header__left">
							<Box display="flex" align="center">
								<div className="layout-dashboard__title__avatar">
									<IconGear />
								</div>
								<div className="layout-dashboard__title__text">
									<div className="layout-dashboard__title__text__top layout-dashboard__main-title">
										Pitstops
									</div>
								</div>
							</Box>
						</div>
					</Box>
					<div className="layout-dashboard__tile__body">
						<PitstopList data={myPitstops || []} />
					</div>
				</div>

				{/* Right column: Data Requests + Use Cases stacked (admin/super-admin only), as the masonry layout placed them */}
				{user?.role?.some(
					(role) =>
						role.name === userRole.SUPER_ADMIN ||
						role.name === userRole.ADMIN
				) && (
					<Box display="flex" direction="column" gap={3}>
						<div className="layout-dashboard__block">
							<Box display="flex" wrap="wrap" align="center" pt={6} pb={4} px={4}>
								<div className="layout-dashboard__tile__header__left">
									<Box display="flex" align="center">
										<div className="layout-dashboard__title__avatar">
											<IconDataRequests />
										</div>
										<div className="layout-dashboard__title__text">
											<div className="layout-dashboard__title__text__top layout-dashboard__main-title">
												Data Requests
											</div>
										</div>
									</Box>
								</div>
							</Box>
							<div className="layout-dashboard__tile__body">
								<DataRequestList data={reviewItems} />
							</div>
							{reviewItems.length > 5 && (
								<div className="layout-dashboard__tile__footer">
									<div className="link">
										<NavLink to="/dashboard/requests-list">
											<strong>View all</strong>
										</NavLink>
									</div>
								</div>
							)}
						</div>

						{isTradex && (
							<div className="layout-dashboard__block">
								<Box display="flex" wrap="wrap" align="center" pt={6} pb={4} px={4}>
									<div className="layout-dashboard__tile__header__left">
										<Box display="flex" align="center">
											<div className="layout-dashboard__title__avatar">
												<IconUseCases />
											</div>
											<div className="layout-dashboard__title__text">
												<div className="layout-dashboard__title__text__top layout-dashboard__main-title">
													Use Cases
												</div>
											</div>
										</Box>
									</div>
								</Box>
								<div className="layout-dashboard__tile__body">
									<UseCaseList data={useCaseList} />
								</div>
								{useCaseList.filter(
									(useCase) => useCase.status === "enrolled"
								).length > 5 && (
									<div className="layout-dashboard__tile__footer">
										<div className="link">
											<NavLink to="/use-cases">
												<strong>View all</strong>
											</NavLink>
										</div>
									</div>
								)}
							</div>
						)}
					</Box>
				)}
			</Grid>

			<RequestConfirmationModal
				visible={visibleConfirmation}
				hide={hideConfirmation}
				title={`${titleCase(requestType)} Request`}
				isReject={requestType === statuses.rejected}
				message={requestType === statuses.approved ? "Are you sure you want to approve this request?" : requestType === statuses.rejected ? "Are you sure you want to reject this request? You will not be able to exchange this data element with this participant until another request is sent." : null}
				onConfirm={requestAction}
			/>

			<Toast toastList={toastList} />
		</>
	);
};

export default Page;
